import Phaser from 'phaser';
import { GAME_FONT_FAMILY, GOLD_TEXT_COLOR, ITEM_RARITY_COLORS, ItemRarity } from '@shared/constants';
import { getItemTemplate, getEffectiveSellPrice } from '@shared/data/items';
import type { ItemDefinition } from '@shared/data/items';
import { findAffixByModifierKey } from '@shared/data/affixes';
import { getIconTexture, getStatIconName, getStatLabelEs, getRarityIconName, formatThousands } from '../../sprites/IconAtlases';

/**
 * Tooltip flotante reusable para items. Se llama con `show(itemId, x, y, extras?)`
 * y se posiciona relativo al pointer (+12, +12) con clamp al viewport.
 *
 * Diseñado para reusarse en MerchantPanel, SpellVendorPanel (items requeridos),
 * y cualquier otro panel que muestre items con icono. Una instancia por escena
 * (singleton de hecho); reseteable.
 *
 * El body es un Container con filas mixtas: stats con sprite prefijo (vía
 * `getStatIconName`) o texto plano (tipo, slot, manos, descripción). El precio
 * de venta usa el sprite `gold`.
 */
const DEPTH = 1300;
const PADDING = 8;
const MAX_WIDTH = 240;
const ROW_H = 13;
const ICON_SIZE = 11;
const ICON_GAP = 4;

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

export interface ItemTooltipExtras {
  /** Línea final destacada: "Tenés: X / Necesitás: Y", precio, etc. */
  footerText?: string;
  footerColor?: string;
  /**
   * Rareza rolled de la instancia (ItemRarity como number). Cuando se pasa,
   * el título del tooltip se pinta con `ITEM_RARITY_COLORS[rolledRarity]`
   * en lugar del amarillo fijo.
   */
  rolledRarity?: number;
  /**
   * Afijos rolled de la instancia. Mapa modifierKey → componentes con valor
   * + tier. Cuando se pasa, el tooltip lista cada affix con su valor real
   * (no los modifiers del template base).
   */
  rolledAffixes?: Record<string, Array<{ v: number; t: number }>>;
  /**
   * Precio de COMPRA del item (vendor → player). Cuando viene, la última
   * fila del tooltip muestra `Compra: [oro] N` en lugar del `Venta: …`
   * default. Usado por MerchantPanel para que el tooltip coincida con el
   * botón "Comprar N · [oro] N".
   */
  buyPrice?: number;
}

/** Una fila del body del tooltip: texto plano o stat con sprite prefijo. */
interface BodyRow {
  /** Nombre del ícono en UI_ICONS (ej. 'physical_damage', 'gold'). */
  icon?: string;
  /**
   * Texto opcional ANTES del ícono (ej. "Precio:"). Si está, el layout
   * de la fila pasa a ser: prefixText + ícono + text. Si no, queda como
   * antes: ícono + text.
   */
  prefixText?: string;
  /** Color del prefixText (default: hereda `color`). */
  prefixColor?: string;
  text: string;
  color?: string;
}

export class ItemTooltip {
  private readonly scene: Phaser.Scene;
  private bg!: Phaser.GameObjects.Rectangle;
  /** Ícono de rareza a la izquierda del título (oculto si no hay rareza). */
  private rarityIcon!: Phaser.GameObjects.Sprite;
  /** Si el item actual tiene ícono de rareza (para no forzar visible sin rareza). */
  private rarityIconActive = false;
  private title!: Phaser.GameObjects.Text;
  private bodyContainer!: Phaser.GameObjects.Container;
  private bodyHeight = 0;
  private bodyWidth = 0;
  private footer!: Phaser.GameObjects.Text;
  private isVisible = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.build();
    this.hide();
  }

  private build(): void {
    this.bg = this.scene.add
      .rectangle(0, 0, 100, 100, 0x000000, 0.92)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x888888)
      .setDepth(DEPTH)
      .setScrollFactor(0);
    this.rarityIcon = this.scene.add
      .sprite(0, 0, '')
      .setOrigin(0, 0.5)
      .setDisplaySize(ICON_SIZE, ICON_SIZE)
      .setDepth(DEPTH + 1)
      .setScrollFactor(0)
      .setVisible(false);
    this.title = this.scene.add
      .text(0, 0, '', withGameFont({
        fontSize: '12px', color: '#ffff88', fontStyle: 'bold',
        wordWrap: { width: MAX_WIDTH },
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH + 1)
      .setScrollFactor(0);
    this.bodyContainer = this.scene.add
      .container(0, 0, [])
      .setDepth(DEPTH + 1)
      .setScrollFactor(0);
    this.footer = this.scene.add
      .text(0, 0, '', withGameFont({
        fontSize: '10px', color: '#88ff88', fontStyle: 'bold',
      }))
      .setOrigin(0, 0)
      .setDepth(DEPTH + 1)
      .setScrollFactor(0);
  }

  /**
   * Muestra el tooltip para el item dado en la posición (x, y) del pointer.
   * Si el item no existe, muestra un mensaje genérico.
   */
  show(itemId: string, pointerX: number, pointerY: number, extras?: ItemTooltipExtras): void {
    const tmpl = getItemTemplate(itemId);
    if (!tmpl) {
      this.rarityIcon.setVisible(false);
      this.title.setText(`Item desconocido (${itemId})`).setColor('#ffff88');
      this.rebuildBody([]);
      this.footer.setText('');
    } else {
      const rarityColor =
        extras?.rolledRarity !== undefined
          ? this.colorFor(extras.rolledRarity as ItemRarity)
          : '#ffff88';
      this.applyRarityIcon(extras?.rolledRarity);
      this.title.setText(tmpl.name).setColor(rarityColor);
      this.rebuildBody(buildBodyRows(tmpl, extras?.rolledAffixes, extras?.buyPrice));

      if (extras?.footerText) {
        this.footer
          .setText(extras.footerText)
          .setColor(extras.footerColor ?? '#88ff88');
      } else {
        this.footer.setText('');
      }
    }
    this.layout(pointerX, pointerY);
    this.setAllVisible(true);
    this.isVisible = true;
  }

  hide(): void {
    if (!this.isVisible && !this.bg) return;
    this.setAllVisible(false);
    this.isVisible = false;
  }

  destroy(): void {
    this.bg?.destroy();
    this.rarityIcon?.destroy();
    this.title?.destroy();
    this.bodyContainer?.destroy();
    this.footer?.destroy();
  }

  isShown(): boolean {
    return this.isVisible;
  }

  /**
   * Convierte el ITEM_RARITY_COLORS (number) a string hex. Si rarity inválida,
   * fallback amarillo.
   */
  private colorFor(rarity: ItemRarity): string {
    const num = ITEM_RARITY_COLORS[rarity];
    if (typeof num !== 'number') return '#ffff88';
    return `#${num.toString(16).padStart(6, '0')}`;
  }

  /** Setea (o oculta) el ícono de rareza a la izquierda del título. */
  private applyRarityIcon(rarity: number | undefined): void {
    const name = rarity !== undefined ? getRarityIconName(rarity) : null;
    const tex = name ? getIconTexture(name) : null;
    if (tex && this.scene.textures.exists(tex.textureKey)) {
      this.rarityIcon.setTexture(tex.textureKey, tex.frame).setVisible(true);
      this.rarityIconActive = true;
    } else {
      this.rarityIcon.setVisible(false);
      this.rarityIconActive = false;
    }
  }

  /**
   * Limpia el body container y lo reconstruye con las filas dadas. Cada fila
   * es Text(texto) o Sprite(ícono) + Text(texto), una por línea, alineadas
   * al margen izquierdo.
   *
   * Computa `bodyHeight` y `bodyWidth` para que `layout()` pueda dimensionar
   * el bg correctamente.
   */
  private rebuildBody(rows: BodyRow[]): void {
    this.bodyContainer.removeAll(true);
    let maxW = 0;
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const y = i * ROW_H;
      let cursorX = 0;

      // Orden de render por fila: [ícono] [prefixText bold] [text]
      // Ej.: "[espada] Daño mínimo: 5"
      //      "[oro] 100"
      if (row.icon) {
        const tex = getIconTexture(row.icon);
        if (tex && this.scene.textures.exists(tex.textureKey)) {
          const sprite = this.scene.add
            .sprite(cursorX, y + ICON_SIZE / 2, tex.textureKey, tex.frame)
            .setOrigin(0, 0.5)
            .setDisplaySize(ICON_SIZE, ICON_SIZE)
            .setScrollFactor(0);
          this.bodyContainer.add(sprite);
          cursorX += ICON_SIZE + ICON_GAP;
        }
      }

      if (row.prefixText) {
        const prefix = this.scene.add
          .text(cursorX, y + ICON_SIZE / 2, `${row.prefixText} `, withGameFont({
            fontSize: '10px', color: row.prefixColor ?? row.color ?? '#cccccc',
            fontStyle: 'bold',
          }))
          .setOrigin(0, 0.5)
          .setScrollFactor(0);
        this.bodyContainer.add(prefix);
        cursorX += prefix.width;
      }

      const text = this.scene.add
        .text(cursorX, y + ICON_SIZE / 2, row.text, withGameFont({
          fontSize: '10px', color: row.color ?? '#cccccc',
          wordWrap: { width: MAX_WIDTH - cursorX },
        }))
        .setOrigin(0, 0.5)
        .setScrollFactor(0);
      this.bodyContainer.add(text);
      maxW = Math.max(maxW, cursorX + text.width);
    }
    this.bodyHeight = rows.length * ROW_H;
    this.bodyWidth = maxW;
  }

  /** Computa el tamaño y posiciona los elementos en (x+offset, y+offset). */
  private layout(pointerX: number, pointerY: number): void {
    const titleH = this.title.height;
    const bodyH = this.bodyHeight;
    const footerH = this.footer.text ? this.footer.height : 0;
    const gap = 4;
    // Ancho extra que ocupa el ícono de rareza a la izquierda del título.
    const titleIconW = this.rarityIcon.visible ? ICON_SIZE + ICON_GAP : 0;
    const innerH = titleH + (bodyH ? gap + bodyH : 0) + (footerH ? gap + footerH : 0);
    const innerW = Math.max(titleIconW + this.title.width, this.bodyWidth, this.footer.width, 80);
    const w = innerW + PADDING * 2;
    const h = innerH + PADDING * 2;

    // Posicionar offset del cursor; clamp al viewport.
    const camW = this.scene.scale.width;
    const camH = this.scene.scale.height;
    let x = pointerX + 12;
    let y = pointerY + 12;
    if (x + w > camW) x = pointerX - w - 12;
    if (y + h > camH) y = camH - h - 4;
    if (x < 4) x = 4;
    if (y < 4) y = 4;

    this.bg.setPosition(x, y).setSize(w, h);
    this.title.setPosition(x + PADDING + titleIconW, y + PADDING);
    if (this.rarityIcon.visible) {
      this.rarityIcon.setPosition(x + PADDING, y + PADDING + titleH / 2);
    }
    this.bodyContainer.setPosition(x + PADDING, y + PADDING + titleH + (bodyH ? gap : 0));
    this.footer.setPosition(
      x + PADDING,
      y + PADDING + titleH + (bodyH ? gap + bodyH : 0) + (footerH ? gap : 0)
    );
  }

  private setAllVisible(visible: boolean): void {
    this.bg.setVisible(visible);
    this.rarityIcon.setVisible(visible && this.rarityIconActive);
    this.title.setVisible(visible);
    this.bodyContainer.setVisible(visible);
    this.footer.setVisible(visible);
  }
}

/**
 * Arma las filas estructuradas del cuerpo: tipo/subtipo/slot, manos, descripción
 * y stats. Cada stat con ícono prefijo (vía `getStatIconName`). Si `rolledAffixes`
 * viene, usa los stats rolados de la instancia. El precio de venta usa el ícono `gold`.
 */
function buildBodyRows(
  tmpl: ItemDefinition,
  rolledAffixes?: Record<string, Array<{ v: number; t: number }>>,
  buyPrice?: number,
): BodyRow[] {
  const rows: BodyRow[] = [];
  const typeBits: string[] = [];
  if (tmpl.type) typeBits.push(tmpl.type);
  if (tmpl.subtype) typeBits.push(tmpl.subtype);
  // Si el subtype es un weapon subtype conocido (sword/axe/dagger/staff/wand/
  // bow/crossbow/throwable), prefijamos la línea con el ícono correspondiente.
  // Los nombres en atlas 2 calzan 1:1 con `WeaponSubtype.*`; si el subtype no
  // tiene ícono mapeado (ej. 'knuckles'), el resolver devuelve null y el
  // render simplemente omite el sprite.
  if (typeBits.length > 0) {
    rows.push({ text: typeBits.join(' / '), icon: tmpl.subtype });
  }
  if (tmpl.slot) rows.push({ text: `Slot: ${tmpl.slot}` });
  if (tmpl.weaponHand) {
    rows.push({ text: `Manos: ${tmpl.weaponHand === 'two' ? '2 manos' : '1 mano'}` });
  }
  if (tmpl.description) rows.push({ text: tmpl.description });

  // Nivel de Herrería requerido (metadata estilo OSRS). Informativo: no gatea
  // nada hasta que exista el sistema de skills. Ver docs/SMITHING_TIERS.md.
  if (typeof tmpl.requiredSmithingLevel === 'number') {
    rows.push({
      prefixText: 'Requiere Herrería:',
      text: `${tmpl.requiredSmithingLevel}`,
      color: '#e0a860',
    });
  }

  // Stats: preferir rolled affixes; sino caer a modifiers base.
  // Formato unificado: `[ícono] <Título traducido bold>: <valor>` — el
  // prefixText es el título bold (heredando el color del row).
  if (rolledAffixes && Object.keys(rolledAffixes).length > 0) {
    for (const [modKey, components] of Object.entries(rolledAffixes)) {
      const total = components.reduce((sum, c) => sum + c.v, 0);
      const tierLabel = components.length === 1 ? ` (T${components[0].t})` : '';
      const affixDef = findAffixByModifierKey(modKey);
      const isPercent = affixDef?.displayFormat === 'percent';
      const valStr = isPercent ? `${Math.round(total * 100)}%` : formatNumber(total);
      rows.push({
        icon: getStatIconName(modKey) ?? undefined,
        prefixText: `${getStatLabelEs(modKey)}:`,
        text: `${valStr}${tierLabel}`,
        color: '#aaffaa',
      });
    }
  } else {
    const mods = tmpl.modifiers ?? {};
    for (const [key, val] of Object.entries(mods)) {
      if (typeof val !== 'number' || val === 0) continue;
      rows.push({
        icon: getStatIconName(key) ?? undefined,
        prefixText: `${getStatLabelEs(key)}:`,
        text: `${val}`,
        color: '#aaffaa',
      });
    }
  }

  // Cuando viene `buyPrice` (estamos en vendor), mostramos `[oro] N` (precio
  // de compra). Sino, el tooltip es genérico (hover desde inventory u otro
  // lado) y mostramos `[oro] N` con el sellPrice. Sin label "Compra:" /
  // "Venta:" — el ícono del oro ya indica que es moneda.
  if (typeof buyPrice === 'number' && buyPrice > 0) {
    rows.push({
      icon: 'gold',
      text: formatThousands(buyPrice),
      color: GOLD_TEXT_COLOR,
    });
  } else {
    const sellPrice = getEffectiveSellPrice(tmpl);
    if (sellPrice > 0) {
      rows.push({
        icon: 'gold',
        text: formatThousands(sellPrice),
        color: GOLD_TEXT_COLOR,
      });
    }
  }

  return rows;
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, '');
}
